import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import { Save, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin: configure the Telegram support FAB that appears on the public site
 * (right next to / below the Live Chat button). Master toggle + URL + label +
 * tagline. The button is rendered server-side ONLY when enabled AND a URL is
 * present.
 */
export default function ConfigTelegramSupport() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings/telegram-support");
      setForm(data);
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.enabled && !(form.url || "").trim()) {
      toast.error("Please add a Telegram URL or @username before enabling.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/telegram-support", {
        enabled: !!form.enabled,
        url: form.url || "",
        label: form.label || "Telegram Support",
        tagline: form.tagline || "",
        open_in_new_tab: form.open_in_new_tab !== false,
      });
      setForm(data);
      toast.success("Telegram support settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;
  }

  return (
    <div data-testid="config-telegram-support-page">
      <PageHeader
        title="Configuration"
        subtitle="Site-wide configuration · Telegram Support · floating button on the public site"
      />
      <ConfigTabs />

      <form
        onSubmit={(e) => { e.preventDefault(); save(); }}
        className="bg-white border border-gray-200 rounded-sm p-6 max-w-4xl space-y-6"
        data-testid="config-telegram-support-form"
      >
        {/* Master toggle */}
        <div className="flex items-start gap-4 p-4 border border-gray-200 rounded-sm bg-gray-50">
          <div className="h-10 w-10 rounded-sm bg-sky-500/10 text-sky-600 inline-flex items-center justify-center shrink-0">
            <Send size={20} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-gray-900">Show Telegram support button on site</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  When enabled, a floating Telegram button appears below the live chat button on every public page.
                </div>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={(e) => set("enabled", e.target.checked)}
                  className="sr-only peer"
                  data-testid="tg-support-enabled"
                />
                <span className="relative w-11 h-6 bg-gray-300 peer-checked:bg-sky-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></span>
              </label>
            </div>
          </div>
        </div>

        {/* URL */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Telegram URL or @username <span className="text-rose-500">*</span>
          </label>
          <input
            value={form.url || ""}
            onChange={(e) => set("url", e.target.value)}
            placeholder="@maxlive88support  ·  https://t.me/yourchannel  ·  tg://resolve?domain=foo"
            className="w-full h-11 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-sky-500 font-mono"
            data-testid="tg-support-url"
          />
          <div className="text-[11px] text-gray-500 mt-1.5">
            Accepts <code className="bg-gray-100 px-1 rounded">@username</code>, <code className="bg-gray-100 px-1 rounded">t.me/foo</code>, full <code className="bg-gray-100 px-1 rounded">https://t.me/...</code> URL or a <code className="bg-gray-100 px-1 rounded">tg://</code> deep-link. Will be normalized to a canonical https URL on save.
          </div>
          {form.url && (
            <a
              href={form.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-sky-600 hover:text-sky-700 font-semibold"
              data-testid="tg-support-test-link"
            >
              <ExternalLink size={12} /> Test this link in a new tab
            </a>
          )}
        </div>

        {/* Label + tagline */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
              Button label (tooltip)
            </label>
            <input
              value={form.label || ""}
              onChange={(e) => set("label", e.target.value)}
              placeholder="Telegram Support"
              maxLength={60}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-sky-500"
              data-testid="tg-support-label"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
              Tagline (sub-text on hover)
            </label>
            <input
              value={form.tagline || ""}
              onChange={(e) => set("tagline", e.target.value)}
              placeholder="24/7 instant chat on Telegram"
              maxLength={120}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-sky-500"
              data-testid="tg-support-tagline"
            />
          </div>
        </div>

        {/* Open behaviour */}
        <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm">
          <input
            id="tg-support-open-newtab"
            type="checkbox"
            checked={form.open_in_new_tab !== false}
            onChange={(e) => set("open_in_new_tab", e.target.checked)}
            className="mt-1"
            data-testid="tg-support-open-newtab"
          />
          <label htmlFor="tg-support-open-newtab" className="cursor-pointer">
            <div className="text-sm font-semibold text-gray-900">Open in a new browser tab</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Recommended. Mobile devices will still try to open the Telegram app first if installed.
            </div>
          </label>
        </div>

        {/* Live preview pill */}
        {form.enabled && form.url && (
          <div className="p-4 border border-sky-200 bg-sky-50 rounded-sm">
            <div className="text-[10px] uppercase tracking-wider text-sky-600 font-bold mb-2">Live Preview</div>
            <div className="flex items-center gap-3">
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center"
                style={{
                  background: "radial-gradient(circle at 32% 26%, #7dd3fc 0%, #0ea5e9 45%, #075985 100%)",
                  boxShadow: "0 8px 22px rgba(14,165,233,0.45), 0 0 0 1.5px rgba(56,189,248,0.55)",
                }}
              >
                <Send size={20} className="text-white -ml-0.5" />
              </div>
              <div>
                <div className="text-sm font-bold text-gray-900">{form.label || "Telegram Support"}</div>
                {form.tagline && <div className="text-xs text-gray-500">{form.tagline}</div>}
                <div className="text-[10px] text-gray-500 mt-0.5 font-mono break-all">{form.url}</div>
              </div>
            </div>
          </div>
        )}

        {/* Save */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            Last updated: {form.updated_at ? new Date(form.updated_at).toLocaleString() : "—"}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="h-10 px-5 bg-sky-500 hover:bg-sky-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="tg-support-save"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Telegram Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
